import { useState } from 'react';
import { DocumentType, PackageTypeId } from '@models/document';
import { useStore } from '@store/GlobalStore';
import { shallow } from 'zustand/shallow';
import { QuoteRating } from '@models/quoteRating';
import { documentGenerationAllowedRoles } from '@pages/BindQuote/QuoteProposalCard';
import { useSearchQuoteRatings } from '@services/quoteRating';
import { useSearchSubjectivities } from '@services/subjectivity';
import { arraysOverlap } from '@utils/arrays';
import { writingCompanyIds } from '@models/productOffering';
import { DeclarationType } from '@lio/rule-engine';
import { useQuote } from '@services/quotes';
import { useCreateQuoteDeclaration } from '@services/quotes/quoteDeclaration';
import { useHasMissingRequiredBeforeBindFields } from '@hooks/useHasMissingRequiredBeforeBindFields';

import { Button, IButtonProps } from '../Button';
import { useDownloadQuoteDocument } from './useDownloadQuoteDocument';
import {
  IncompleteProposalNoticeModal,
  INCOMPLETE_PROPOSAL_NOTICE_TEXT,
  INCOMPLETE_PROPOSAL_CHECKBOX_TEXT,
} from './IncompleteProposalNoticeModal';

export type DownloadApplicationButtonProps = {
  quoteId: string;
  buttonProps?: IButtonProps;
};

export const DownloadProposalButton = ({ quoteId, buttonProps }: DownloadApplicationButtonProps) => {
  const [roles, { isInternalUser }] = useStore((state) => [state.roles, state.userAuth], shallow);
  const { quote } = useQuote(quoteId);
  const { createQuoteDeclaration, isPending: isCreatingDeclaration } = useCreateQuoteDeclaration(quoteId);
  const hasMissingRequiredBeforeBindFields = useHasMissingRequiredBeforeBindFields();

  const [showNoticeModal, setShowNoticeModal] = useState(false);

  const isUserRoleDisabled = !arraysOverlap(roles, documentGenerationAllowedRoles);
  const { data: searchQuoteRatings, isPending: isSearchQuoteRatingsLoading } = useSearchQuoteRatings({
    quoteId,
    enabled: true,
    params: { pageLimit: 10 },
  });

  const quoteRatings = searchQuoteRatings?.pages.flatMap<QuoteRating>((page) => page.items);
  const { subjectivities, isPending: isSearchSubjectivitiesLoading } = useSearchSubjectivities({ quoteId });

  const initialDate = subjectivities?.items.length ? subjectivities.items[0].updatedAt : undefined;
  const latestSubjectivityUpdate = subjectivities?.items.reduce((latest, item) => {
    return latest && new Date(item.updatedAt) > new Date(latest) ? item.updatedAt : latest;
  }, initialDate);

  const isRatingNewerThanProposal = quoteRatings?.[0]?.createdAt
    ? new Date(quoteRatings?.[0]?.createdAt).getTime()
    : undefined;

  const isLatestSubjectivityUpdateNewerThanProposal = latestSubjectivityUpdate
    ? new Date(latestSubjectivityUpdate).getTime()
    : undefined;

  const mostRecentDate =
    isRatingNewerThanProposal && isLatestSubjectivityUpdateNewerThanProposal
      ? new Date(Math.max(isRatingNewerThanProposal, isLatestSubjectivityUpdateNewerThanProposal))
      : isRatingNewerThanProposal
        ? new Date(isRatingNewerThanProposal)
        : isLatestSubjectivityUpdateNewerThanProposal
          ? new Date(isLatestSubjectivityUpdateNewerThanProposal)
          : undefined;

  const downloadType = 'manual';
  const { isLoading, downloadUrl, isDisabled, handleDownloadQuoteDocument, documentFormSetId } =
    useDownloadQuoteDocument({
      quoteId,
      createdOnOrAfterDate: mostRecentDate,
      documentTypeId: DocumentType.QuoteProposal.id,
      packageTypeId: PackageTypeId.QuoteProposal,
      downloadType,
      disabled: isUserRoleDisabled || isSearchQuoteRatingsLoading || isSearchSubjectivitiesLoading,
    });

  const isLSIC = quote?.writingCompany?.id === writingCompanyIds.specialty;
  const shouldShowNotice = !isInternalUser && isLSIC && hasMissingRequiredBeforeBindFields;

  const handleDownloadApplicationClick = () => {
    if (shouldShowNotice && downloadUrl) {
      setShowNoticeModal(true);
      return;
    }
    handleDownloadQuoteDocument();
  };

  const handleAcceptAndDownload = async () => {
    try {
      await createQuoteDeclaration({
        declarationType: DeclarationType.IncompleteProposalNotice,
        declaration: {
          statements: [INCOMPLETE_PROPOSAL_NOTICE_TEXT, INCOMPLETE_PROPOSAL_CHECKBOX_TEXT],
        },
        documentFormSetId: documentFormSetId ?? undefined,
      });
    } catch {
      return;
    }
    setShowNoticeModal(false);

    if (downloadUrl) {
      const link = document.createElement('a');
      link.target = '_blank';
      link.href = downloadUrl;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      handleDownloadQuoteDocument();
    }
  };

  const proposalOrIndication = shouldShowNotice ? 'Indication' : 'Proposal';
  const display =
    (!downloadUrl && downloadType === 'manual') || isSearchQuoteRatingsLoading || isSearchSubjectivitiesLoading
      ? `Create ${proposalOrIndication}`
      : `Download ${proposalOrIndication}`;

  return (
    <>
      <Button
        data-testid='Download-Application'
        target='_blank'
        href={shouldShowNotice ? undefined : downloadUrl}
        isLoading={isLoading}
        onClick={() => (shouldShowNotice || !downloadUrl) && handleDownloadApplicationClick()}
        variant={buttonProps?.variant ?? 'contained'}
        disabled={isDisabled || isUserRoleDisabled || isSearchQuoteRatingsLoading || isSearchSubjectivitiesLoading}
        {...buttonProps}
      >
        {isSearchQuoteRatingsLoading ? '' : display}
      </Button>
      <IncompleteProposalNoticeModal
        isOpen={showNoticeModal}
        isLoading={isCreatingDeclaration}
        onClose={() => setShowNoticeModal(false)}
        onAccept={handleAcceptAndDownload}
      />
    </>
  );
};
